/*
 * Copyright (C) 2014 Spanning Cloud Apps.  All rights reserved.
 */
import * as _l from 'lodash';

import './selectview.tpl.html';

angular
  .module('bulkExport.selectview', [
    'services.bulkrestore',
    'services.bulkrestoredata',
    'services.error',
    'services.util'
  ])
  // These keys are used for sorting the records
  .constant(
    'SortKeys',
    Object.freeze({
      NAME: 'sfName',
      SFID: 'sfId'
    })
  )
  /**
   * Controller for the bulkExport.selectview template.
   */
  .controller('BulkExportSelectViewCtrl', [
    '$scope',
    '$uibModal',
    '$filter',
    '$q',
    '$rootScope',
    '$state',
    'batchUtils',
    'httpBulkRestore',
    'httpQueue',
    'objectTypes', // Resolved in restore.js state config
    'progressFactory',
    'BulkRestoreObjectListType',
    'BulkRestoreService',
    'ErrorService',
    'MomentFormats',
    'SortKeys',
    'UtilService',
    'WizardButtonFactory',
    'WizardService',
    'SOBJECT_STATE',
    'TYPES',
    'VIEW_STATES',
    'confirmModalUtils',
    'configService',
    function (
      $scope,
      $uibModal,
      $filter,
      $q,
      $rootScope,
      $state,
      batchUtils,
      httpBulkRestore,
      httpQueue,
      objectTypes, // Resolved in restore.js state config
      progressFactory,
      BulkRestoreObjectListType,
      BulkRestoreService,
      ErrorService,
      MomentFormats,
      SortKeys,
      UtilService,
      WizardButtonFactory,
      WizardService,
      SOBJECT_STATE,
      TYPES,
      VIEW_STATES,
      confirmModalUtils,
      configService
    ) {
      if (!$state.params.bulkRestoreData) {
        throw new Error('bulkRestoreData is required');
      }

      const i18n = $filter('i18n');
      const pluralize = $filter('pluralize');
      const bulkRestoreData = $state.params.bulkRestoreData;
      const searchRecordsPageSize = 20;
      let modalInstance, touched, model;

      // Published functions
      $scope.cancelEdit = cancelEdit;
      $scope.canSubmitSearch = canSubmitSearch;
      $scope.confirmLossOfChanges = confirmLossOfChanges;
      $scope.getBatchDetailsTitle = getBatchDetailsTitle;
      $scope.getHeaderClass = getHeaderClass;
      $scope.getObjectRecordStatus = getObjectRecordStatus;
      $scope.getObjectStateLabel = getObjectStateLabel;
      $scope.getObjectTypeIconClass = getObjectTypeIconClass;
      $scope.getSearchItemsFoundText = getSearchItemsFoundText;
      $scope.getSubmitSearchTooltip = getSubmitSearchTooltip;
      $scope.hasAllCriteria = hasAllCriteria;
      $scope.hasEnoughBackups = hasEnoughBackups;
      $scope.hasSearchChanges = hasSearchChanges;
      $scope.hasSearchResults = hasSearchResults;
      $scope.initWizardPage = initWizardPage;
      $scope.isEndBatchChanged = isEndBatchChanged;
      $scope.isObjectStatesChanged = isObjectStatesChanged;
      $scope.isObjectTypeChanged = isObjectTypeChanged;
      $scope.isRestoreFromBackup = isRestoreFromBackup;
      $scope.isSearch = isSearch;
      $scope.isSearchTextChanged = isSearchTextChanged;
      $scope.isStartSystemModstampChanged = isStartSystemModstampChanged;
      $scope.isEndSystemModstampChanged = isEndSystemModstampChanged;
      $scope.isStartBatchChanged = isStartBatchChanged;
      $scope.onRequestedObjectTypeChanged = onRequestedObjectTypeChanged;
      $scope.onSelectedObjectTypesChanged = onSelectedObjectTypesChanged;
      $scope.setSearchRecordsResult = setSearchRecordsResult;
      $scope.showPreview = showPreview;
      $scope.sortRecords = sortRecords;
      $scope.submitBackup = submitBackup;
      $scope.submitSearch = submitSearch;
      $scope.toggleSelectingAll = toggleSelectingAll;
      $scope.updateLastSearch = updateLastSearch;
      $scope.searchRecordsPage = searchRecordsPage;
      $scope.watchScopeDestroy = watchScopeDestroy;
      $scope.watchSearchEndBatchId = watchSearchEndBatchId;
      $scope.watchSearchStartBatchId = watchSearchStartBatchId;
      $scope.watchRequestsInProgress = watchRequestsInProgress;
      $scope.watchSearchObjectState = watchSearchObjectState;

      // Publish the bulk restore data
      $scope.bulkRestoreData = bulkRestoreData;
      $scope.BulkRestoreObjectListType = BulkRestoreObjectListType;
      $scope.SortKeys = SortKeys;
      $scope.dateFormat = 'MMM d, y h:mm a'; // This is an Angular date format, not moment

      // Set up a map of the labels to be used for each object state
      $scope.objectStateLabelMap = {
        [SOBJECT_STATE.UPDATED]: i18n('UPDATED'),
        [SOBJECT_STATE.DELETED]: i18n('DELETED'),
        [SOBJECT_STATE.NEW]: i18n('CREATED'),
        [SOBJECT_STATE.UNCHANGED]: i18n('UNCHANGED')
      };

      $scope.canSearch = true;

      // Save the most recent search so we can tell whether the criteria have changed
      $scope.lastSearch = {
        objectType: undefined,
        searchText: undefined,
        objectStates: undefined,
        startBatchId: undefined,
        endBatchId: undefined,
        startSystemModstamp: undefined,
        endSystemModstamp: undefined
      };

      configService.getConfig().then((config) => {
        $scope.searchSortAvailable = config.searchSortAvailable;
      });

      // Initialize and monitor the object state
      if (!bulkRestoreData.getSearchObjectStates()) {
        bulkRestoreData.setSearchObjectStates([SOBJECT_STATE.UPDATED]);
      }

      // We use '' as the default because the input field returns '' when you type and then clear the value.
      if (!$scope.isRestoreFromBackup() && bulkRestoreData.getSearchText() === undefined) {
        bulkRestoreData.setSearchText('');
      }

      // Store data in a model object rather than the scope itself for easier watching
      $scope.model = model = {
        // True if the user is editing the search criteria. Start in edit mode if there are no search results.
        editing: !$scope.isRestoreFromBackup() && !$scope.hasSearchResults(),
        hasCompletedSearch: $scope.hasSearchResults(),
        objectTypes: objectTypes,
        previewModalOpened: false,
        searchEndBatchDate: undefined, // The batch picker sets this for us
        searchEndBatchId: bulkRestoreData.getSearchEndBatchId(),
        searchObjectStates: bulkRestoreData.getSearchObjectStates(),
        searchObjectType: bulkRestoreData.getSearchObjectType(),
        searchStartBatchDate: undefined, // The batch picker sets this for us
        searchStartBatchId: bulkRestoreData.getSearchStartBatchId(),
        // For the purposes of confirming cancellation of the wizard we'll consider the wizard touched if we get this
        // far.
        touched: true,
        lastPage: bulkRestoreData.getTotalObjectRecordCount().count <= bulkRestoreData.getPageObjectRecords().length,
        ascending: undefined,
        sortBy: undefined
      };

      // We will use $scope.searchObjectState value only to be consistent for single selection on ui. The source of
      // truth will be model.searchObjectStates
      if (model.searchObjectStates && model.searchObjectStates.length) {
        $scope.model.searchObjectState = bulkRestoreData.getSearchObjectStates()[0];
      } else {
        $scope.model.searchObjectState = SOBJECT_STATE.UPDATED;
      }

      // See if we've already run a search (we're coming back to this page using the Prev button)
      if ($scope.hasSearchResults()) {
        $scope.updateLastSearch();
      }

      $scope.initWizardPage();
      $scope.watchScopeDestroy();
      $scope.watchRequestsInProgress();
      $scope.watchSearchObjectState();

      // Display the initial record list
      if ($scope.isRestoreFromBackup()) {
        // We may already have the search results if we're coming back to this page using the Prev button
        if (!$scope.hasSearchResults()) {
          $scope.searchRecordsPage();
        }
      } else {
        $scope.watchSearchStartBatchId();
        $scope.watchSearchEndBatchId();
      }

      /**
       * Cancels the search criteria edit in progress. Asks for confirmation if there are changes to the search criteria.
       *
       * @returns {Promise} the promise of completion.
       */
      function cancelEdit() {
        let promise;
        if ($scope.hasSearchChanges()) {
          promise = $scope.confirmLossOfChanges();
        } else {
          promise = $q.when(/*confirmed:*/ true);
        }

        return promise.then((confirmed) => {
          if (confirmed) {
            // Reset the settings
            model.searchObjectType = $scope.lastSearch.objectType;
            model.searchObjectStates = $scope.lastSearch.objectStates;
            model.searchStartBatchId = $scope.lastSearch.startBatchId;
            model.searchEndBatchId = $scope.lastSearch.endBatchId;

            bulkRestoreData.setSearchObjectType(model.searchObjectType);
            bulkRestoreData.setSearchText($scope.lastSearch.searchText);
            bulkRestoreData.setSearchObjectStates(model.searchObjectStates);
            bulkRestoreData.setSearchStartBatchId(model.searchStartBatchId);
            bulkRestoreData.setSearchEndBatchId(model.searchEndBatchId);

            model.editing = false;
          }
        });
      }

      /**
       * Determines whether a search can (and needs to be) submitted.
       *
       * @returns {boolean} true if a search can be submitted.
       */
      function canSubmitSearch() {
        return $scope.canSearch && $scope.hasAllCriteria() && (!model.hasCompletedSearch || $scope.hasSearchChanges());
      }

      /**
       * Displays a confirmation dialog when canceling the criteria edits and there are changes.
       *
       * @returns {Promise<boolean>} The promise of a boolean indicating whether the operation was confirmed (true) or not
       * (false).
       */
      function confirmLossOfChanges() {
        // Scroll to the top since that's where the modal will appear.
        UtilService.scrollHome();

        return confirmModalUtils.openConfirmModal({
          confirmTextKey: 'RESTORE_SEARCH_UNSAVED_CHANGES_MSG',
          titleTextKey: 'RESTORE_SEARCH_UNSAVED_CHANGES_TITLE'
        });
      }

      /**
       * Determines the title string displayed for this restore. Only applies to BACKUP restores.
       *
       * @returns {string} the title string.
       */
      function getBatchDetailsTitle() {
        const pageRecords = bulkRestoreData.getPageObjectRecords();
        const length = Array.isArray(pageRecords) ? pageRecords.length : 0;
        const recordText = pluralize(length, 'RECORD', 'RECORDS').toLowerCase();
        let objectStateStr = getStatus();
        objectStateStr = objectStateStr.toLowerCase();
        const dateTimeStr = moment(bulkRestoreData.getBackupBatchDate()).format(MomentFormats.BATCH);

        return i18n('RESTORE_BATCH_DETAILS_TITLE', [model.searchObjectType, recordText, objectStateStr, dateTimeStr]);
      }

      /**
       * Returns the class to be used to style a header in the record list.
       *
       * @param {string} sortKey - The sort key used to identify the header.
       * @returns {string} The class to use.
       */
      function getHeaderClass(sortKey) {
        let headerClass = '';
        if ($scope.model.sortBy === sortKey && $scope.searchSortAvailable) {
          if ($scope.model.ascending) {
            headerClass = 'sbsf-log-header-asc sca-icon-down-black';
          } else {
            headerClass = 'sbsf-log-header-desc sca-icon-down-black';
          }
        }
        return headerClass;
      }

      /**
       * Returns the status for a record object.
       *
       * @param {object} record - the record to be checked.
       * @returns {string} - the status, either new, unchanged, deleted or updated.
       */
      function getObjectRecordStatus(record) {
        if (bulkRestoreData.isManualObjectBrowse()) {
          return i18n('IN_BACKUP');
        }
        let searchObjectStates = $scope.lastSearch.objectStates || bulkRestoreData.getSearchObjectStates();
        if (record && record.isDeleted) {
          return i18n('DELETED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.UPDATED) >= 0) {
          return i18n('UPDATED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.NEW) >= 0) {
          return i18n('NEW_OR_UPDATED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.UNCHANGED) >= 0) {
          return i18n('UNCHANGED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.DELETED) >= 0) {
          return i18n('DELETED');
        }
        return i18n('UPDATED');
      }

      /**
       * Gets the label to be shown for the currently selected object state.
       *
       * @returns {string} the object state label
       */
      function getObjectStateLabel() {
        return $scope.objectStateLabelMap[model.searchObjectStates];
      }

      /**
       * Returns the CSS icon class to use for the currently selected object type.
       *
       * @returns {string} the CSS icon class to use
       */
      function getObjectTypeIconClass() {
        return TYPES.typeToCls[bulkRestoreData.getSearchObjectType()];
      }

      /**
       * Returns the text to be displayed next to the search box showing how many items were found and selected.
       *
       * @returns {string} the text to be displayed.
       */
      function getSearchItemsFoundText() {
        const recordsToRestoreCount = bulkRestoreData.getSelectedObjectRecordsCountInfo();
        const totalCount = bulkRestoreData.getTotalObjectRecordCount();
        let text = totalCount.approximate ? `${i18n('ABOUT')} ` : '';
        if (totalCount.thresholdAchieved) {
          text += i18n('ITEMS_FOUND', `${totalCount.threshold}+`);
        } else {
          text += i18n(totalCount.count === 1 ? 'ITEM_FOUND' : 'ITEMS_FOUND', totalCount.count);
        }
        if (recordsToRestoreCount.count > 0) {
          text += i18n('SELECTED', recordsToRestoreCount.text);
        }

        return text;
      }

      /**
       * Returns the tooltip for the submit search button.
       *
       * @returns {string} the tooltip
       */
      function getSubmitSearchTooltip() {
        var tooltip;

        if (!$scope.hasAllCriteria()) {
          tooltip = i18n('SUBMIT_SEARCH_NO_OBJECT_TYPE_TOOLTIP');
        } else if (!$scope.hasSearchChanges()) {
          tooltip = i18n('SUBMIT_SEARCH_UPDATE_CRITERIA_TOOLTIP');
        } else {
          tooltip = i18n('SUBMIT_SEARCH_TOOLTIP');
        }

        return tooltip;
      }

      /**
       * Determines whether all search criteria are specified.
       *
       * @returns {boolean} true if all criteria have been selected.
       */
      function hasAllCriteria() {
        return !!(
          bulkRestoreData.getSearchObjectType() &&
          bulkRestoreData.getSearchStartBatchId() &&
          bulkRestoreData.getSearchObjectStates().length &&
          bulkRestoreData.getSearchEndBatchId() &&
          ((bulkRestoreData.getStartSystemModstamp() && bulkRestoreData.getEndSystemModstamp()) ||
            (!bulkRestoreData.getStartSystemModstamp() && !bulkRestoreData.getEndSystemModstamp()))
        );
      }

      /**
       * Determines whether there are enough backups to run a restore.
       *
       * @returns {boolean} true if there are enough backups.
       */
      function hasEnoughBackups() {
        if (bulkRestoreData.isManualObjectBrowse()) {
          return batchUtils.backupBatchDots.count >= 1;
        }
        return batchUtils.backupBatchDots.count > 1;
      }

      /**
       * Determines whether there are any changes to the search criteria since the last search.
       *
       * @returns {boolean} true if there are changes to the search criteria.
       */
      function hasSearchChanges() {
        return (
          $scope.isObjectTypeChanged() ||
          $scope.isSearchTextChanged() ||
          $scope.isStartSystemModstampChanged() ||
          $scope.isEndSystemModstampChanged() ||
          $scope.isObjectStatesChanged() ||
          $scope.isStartBatchChanged() ||
          $scope.isEndBatchChanged()
        );
      }

      /**
       * Determines whether there are any search results to display.
       *
       * @returns {boolean} true if there are search results to display.
       */
      function hasSearchResults() {
        const pageRecords = bulkRestoreData.getPageObjectRecords();
        const length = Array.isArray(pageRecords) ? pageRecords.length : 0;
        return length > 0;
      }

      /**
       * Sets up this page for use with the wizard service.
       */
      function initWizardPage() {
        // Register our page with the wizard service
        const page = WizardService.getCurrentPage();
        page.setHeaderText(i18n('SELECT_EXPORT_ITEMS'));
        page.setStateParams($state.params);
        page.setValid(isValid);
        page.setTouched(() => touched);
      }

      /**
       * Determines whether the end batch has been changed in the search criteria since the last search.
       *
       * @returns {Boolean} true if a different end batch has been selected since the last search.
       */
      function isEndBatchChanged() {
        return model.hasCompletedSearch && $scope.lastSearch.endBatchId !== bulkRestoreData.getSearchEndBatchId();
      }

      /**
       * Check if start systemModstamp was changed
       * @returns {boolean|*|boolean}
       */
      function isStartSystemModstampChanged() {
        return (
          model.hasCompletedSearch && $scope.lastSearch.startSystemModstamp !== bulkRestoreData.getStartSystemModstamp()
        );
      }

      /**
       * Check if end systemModstamp was changed
       * @returns {boolean|*|boolean}
       */
      function isEndSystemModstampChanged() {
        return (
          model.hasCompletedSearch && $scope.lastSearch.endSystemModstamp !== bulkRestoreData.getEndSystemModstamp()
        );
      }

      /**
       * Determines whether the object state has been changed in the search criteria since the last search.
       *
       * @returns {Boolean} true if a different object state has been selected since the last search.
       */
      function isObjectStatesChanged() {
        const newStates = bulkRestoreData.getSearchObjectStates();
        return (
          model.hasCompletedSearch &&
          $scope.lastSearch &&
          ($scope.lastSearch.objectStates.length !== newStates.length ||
            _l.difference($scope.lastSearch.objectStates, newStates).length > 0)
        );
      }

      /**
       * Determines whether the object type has been changed in the search criteria since the last search.
       *
       * @returns {Boolean} true if a different object type has been selected since the last search.
       */
      function isObjectTypeChanged() {
        return model.hasCompletedSearch && $scope.lastSearch.objectType !== bulkRestoreData.getSearchObjectType();
      }

      /**
       * Determines whether the user is configuring a restore from a specific backup.
       *
       * @returns {boolean} true if the user is configuring a restore from a specific backup.
       */
      function isRestoreFromBackup() {
        return bulkRestoreData.isRestoreFromBackup();
      }

      /**
       * Determines whether the selected list type is SEARCH.
       *
       * @returns {boolean} true if the user is searching for object records.
       */
      function isSearch() {
        return bulkRestoreData.isManualObjectSearch();
      }

      /**
       * Determines whether the search text has been changed in the search criteria since the last search.
       *
       * @returns {Boolean} true if a different search text has been entered since the last search.
       */
      function isSearchTextChanged() {
        return model.hasCompletedSearch && $scope.lastSearch.searchText !== bulkRestoreData.getSearchText();
      }

      /**
       * Determines whether the start batch has been changed in the search criteria since the last search.
       *
       * @returns {Boolean} true if a different start batch has been selected since the last search.
       */
      function isStartBatchChanged() {
        return model.hasCompletedSearch && $scope.lastSearch.startBatchId !== bulkRestoreData.getSearchStartBatchId();
      }

      /**
       * Determines whether the settings on the page are valid (i.e. Next is allowed in the wizard)
       *
       * @returns {boolean} true if the settings are valid, false otherwise.
       */
      function isValid() {
        // We just need a selection
        return bulkRestoreData.isSelectedAtLeastOneRecord() || bulkRestoreData.checkObjectTypeSelectedCount(1);
      }

      /**
       * Utility to set the current records and additional info.
       *
       * @param {{numberOfRecords: number, records: object[], searchToken: string}} results - The search results.
       */
      function setSearchRecordsResult(results) {
        if (results.count) {
          bulkRestoreData.setTotalObjectRecordCount(results.count);
        }
        bulkRestoreData.setPageObjectRecords(results.records, true);
        if (results.searchToken) {
          bulkRestoreData.setSearchToken(results.searchToken);
        }
        if (results.records.length > 0) {
          bulkRestoreData.setSearchPageToken(results.pageToken);
          bulkRestoreData.setSearchPageBatchId(results.pageBatchId);
        }
      }

      /**
       * Handler for launching the preview modal.
       *
       * @param {object} $event - The event object triggered.
       * @param {number} recordIndex - the selected record index in records array.
       */
      function showPreview($event, recordIndex) {
        $event.preventDefault();
        $event.stopPropagation();

        // Scroll to where the modal will appear
        UtilService.scrollHome();

        modalInstance = $uibModal.open({
          templateUrl: 'bulkRestore/modal/preview.tpl.html',
          controller: 'PreviewRecordCtrl',
          windowClass: 'sbsf-bulkrestore-modal-window sbsf-bulk-restore-modal',
          backdrop: false,
          resolve: {
            bulkRestoreData: () => bulkRestoreData,
            recordIndex: () => recordIndex
          }
        });

        modalInstance.opened.then(() => {
          model.previewModalOpened = true;
        });

        modalInstance.result.finally(() => {
          model.previewModalOpened = false;
          // Go back where we started
          UtilService.scrollToElement(`record-${recordIndex}`);
        });
      }

      /**
       * Sorts the object records by the specified key.
       *
       * @param {string} sortKey - The sort key.
       * @returns {Promise} the promise of completion.
       */
      function sortRecords(sortKey) {
        if (!$scope.searchSortAvailable) {
          return;
        }
        if ($scope.model.sortBy !== sortKey) {
          // Start with ascending when we switch to a new column
          $scope.model.sortBy = sortKey;
          $scope.model.ascending = true;
        } else {
          // Toggle the direction when we stay on the same column
          $scope.model.ascending = !$scope.model.ascending;
        }

        return $scope.submitSearch($scope.model.sortBy, $scope.model.ascending);
      }

      /**
       * Submit a backup to be queued.
       */
      function submitBackup() {
        $rootScope.showLoadingMask = true;

        return httpQueue
          .submitBackup()
          .then(() => {
            progressFactory.backupInProgress = true;

            // Reset the touched flag so we can go to the dashboard without confirmation
            touched = false;
            $state.go(VIEW_STATES.ADMIN_HOME);
          })
          .catch((err) => {
            ErrorService.report(err, i18n('SUBMIT_BACKUP_FAILED'));
            progressFactory.backupInProgress = false;
          })
          .finally(() => {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Queues up an object record search or filters the existing search.
       *
       * @returns {Promise} a promise that resolves when the search is queued or filtered.
       */
      function submitSearch(sortBy, ascending) {
        bulkRestoreData.setSearchPageToken(null);
        bulkRestoreData.setSearchPageBatchId(null);
        bulkRestoreData.setSearchToken(null);
        bulkRestoreData.setLastSearchParams(null);
        bulkRestoreData.clearObjectRecords();
        if (sortBy) {
          bulkRestoreData.setSortBy(sortBy);
          bulkRestoreData.setAscending(ascending);
        } else {
          bulkRestoreData.setSortBy(null);
          bulkRestoreData.setAscending(null);
          $scope.model.sortBy = null;
          $scope.model.ascending = null;
        }

        // watchRestoreSearchInProgress will pick up when the search completes
        return searchRecordsPage().then(() => {
          $scope.updateLastSearch();
        });
      }

      /**
       * Toggles the "select all" value.
       */
      function toggleSelectingAll() {
        bulkRestoreData.setSelectingAll(!bulkRestoreData.isSelectingAll());
      }

      /**
       * Saves the current search criteria as the last search.
       */
      function updateLastSearch() {
        $scope.lastSearch.objectType = bulkRestoreData.getSearchObjectType();
        $scope.lastSearch.searchText = bulkRestoreData.getSearchText();
        $scope.lastSearch.objectStates = [...bulkRestoreData.getSearchObjectStates()];
        $scope.lastSearch.startBatchId = bulkRestoreData.getSearchStartBatchId();
        $scope.lastSearch.endBatchId = bulkRestoreData.getSearchEndBatchId();
        $scope.lastSearch.startSystemModstamp = bulkRestoreData.getStartSystemModstamp();
        $scope.lastSearch.endSystemModstamp = bulkRestoreData.getEndSystemModstamp();

        bulkRestoreData.setLastSearchParams($scope.lastSearch);
      }

      /**
       * Search the records values based on the result returned
       * from the server.
       *
       * @returns {Promise} the promise of completion
       */
      function searchRecordsPage() {
        $rootScope.showLoadingMask = true;

        return BulkRestoreService.searchRecordsPage(bulkRestoreData, searchRecordsPageSize)
          .then((results) => {
            $scope.setSearchRecordsResult(results);
            model.hasCompletedSearch = true;
            model.lastPage = results.records.length < searchRecordsPageSize;
          })
          .catch((err) => {
            ErrorService.report(err, i18n('SEARCH_ITEMS_RECORDS_FAILED'));
          })
          .finally(() => {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Watches for a change to the number of server requests currently in progress.
       */
      function watchRequestsInProgress() {
        $scope.$watch(
          () => batchUtils.requestsInProgress,
          (newVal, oldVal) => {
            if (newVal !== oldVal) {
              $rootScope.showLoadingMask = newVal > 0;
            }
          }
        );
      }

      /**
       * It is called when the user request an object type (click on it).
       */
      function onRequestedObjectTypeChanged(objectType) {
        model.searchObjectType = objectType;
        bulkRestoreData.setSearchObjectType(objectType);
        if ($scope.canSubmitSearch()) {
          $scope.submitSearch();
        }
      }

      /**
       * It is called when the user selects an object type.
       */
      function onSelectedObjectTypesChanged(selectedObjectTypesObj) {
        const selectedObjectTypes = {
          selectedAll: selectedObjectTypesObj.selectedAll,
          selectedList: selectedObjectTypesObj.selectedRecords
        };
        bulkRestoreData.setSelectedObjectTypes(selectedObjectTypes);
        if (bulkRestoreData.checkObjectTypeSelectedCount(1)) {
          $scope.canSearch = false;
          bulkRestoreData.setSearchObjectType(null);
        } else {
          bulkRestoreData.setSelectedObjectTypes(null);
          $scope.canSearch = true;
        }
      }

      /**
       * Watches for the scope to be destroyed so we can clean up.
       */
      function watchScopeDestroy() {
        // Need to close modal if opened.
        $scope.$on('$destroy', () => {
          if (model.previewModalOpened && modalInstance) {
            modalInstance.close();
          }
        });
      }

      /**
       * Watches for a change to the batch specified by the end batch picker.
       */
      function watchSearchEndBatchId() {
        $scope.$watch(
          () => model.searchEndBatchId,
          (newVal, oldVal) => {
            if (newVal !== oldVal) {
              bulkRestoreData.setSearchEndBatchId(model.searchEndBatchId);
            }
          }
        );
      }

      /**
       * Watches for a change to the batch specified by the start batch picker.
       */
      function watchSearchStartBatchId() {
        $scope.$watch(
          () => model.searchStartBatchId,
          (newVal, oldVal) => {
            if (newVal !== oldVal) {
              bulkRestoreData.setSearchStartBatchId(model.searchStartBatchId);
            }
          }
        );
      }

      /**
       * Handler that changes object states list in the bulk restore data.
       */
      function watchSearchObjectState() {
        $scope.$watch(
          () => model.searchObjectState,
          (newVal, oldVal) => {
            if (newVal !== oldVal) {
              const newStatesArray = [newVal];
              model.searchObjectStates = newStatesArray;
              bulkRestoreData.setSearchObjectStates(model.searchObjectStates);
            }
          }
        );
      }

      /**
       * Returned the status for the record object.
       *
       * @param {object} record - the record object.
       * @returns {string} - the status.
       */
      function getStatus(record) {
        let searchObjectStates = bulkRestoreData.getSearchObjectStates();
        if (record && record.isDeleted) {
          return i18n('DELETED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.UPDATED) >= 0) {
          return i18n('UPDATED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.NEW) >= 0) {
          return i18n('NEW_OR_UPDATED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.UNCHANGED) >= 0) {
          return i18n('UNCHANGED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.DELETED) >= 0) {
          return i18n('DELETED');
        }
        return i18n('UPDATED');
      }
    }
  ]);
